import { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useVendorStore } from '../stores/vendorStore';
import { getPartnerBanks } from '../services/api';
import Navbar from '../components/Navbar';
import BankDetailDialogEnhanced from '../components/BankDetailDialogEnhanced';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, Building2, MapPin, Copy, Eye } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_GROUPS = [
  { key: 'Active', label: 'Active Banks', color: 'green' },
  { key: 'Inactive', label: 'Inactive Banks', color: 'slate' },
  { key: 'Freezed', label: 'Freezed Banks', color: 'red' },
];

const PartnerPortal = () => {
  const user = useAuthStore((s) => s.user);
  const { setSelectedBank, setSelectedVendor } = useVendorStore();
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const res = await getPartnerBanks();
        setBanks(res.data);
      } catch (err) { console.error('Failed to fetch partner banks:', err); }
      setLoading(false);
    };
    fetchBanks();
  }, []);

  const handleViewBank = (bank) => {
    setSelectedVendor({ id: bank.vendorId, name: bank.vendorName, banks: [bank] });
    setSelectedBank(bank);
    setShowDetailDialog(true);
  };

  const handleCopy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied!`); }
    catch { toast.error('Failed to copy'); }
  };

  const filtered = banks.filter(b => {
    if (statusFilter !== 'all') {
      if (statusFilter === 'Freezed') {
        if (!b.status?.startsWith('Freezed')) return false;
      } else if (b.status !== statusFilter) return false;
    }
    if (search) {
      const s = search.toLowerCase();
      return [b.bankName, b.region, b.accountNumber, b.vendorName].join(' ').toLowerCase().includes(s);
    }
    return true;
  });

  const activeBanks = banks.filter(b => b.status === 'Active');
  const inactiveBanks = banks.filter(b => b.status === 'Inactive');
  const freezedBanks = banks.filter(b => b.status?.startsWith('Freezed'));

  const getStatusBadge = (status) => {
    const styles = {
      'Active': 'bg-green-100 text-green-800',
      'Inactive': 'bg-slate-100 text-slate-600',
      'Freezed - Transaction': 'bg-red-100 text-red-800',
      'Freezed - CBI': 'bg-red-100 text-red-800',
      'Return': 'bg-orange-100 text-orange-800',
    };
    return <Badge className={styles[status] || 'bg-slate-100 text-slate-600'}>{status}</Badge>;
  };

  if (loading) {
    return (<><Navbar /><div className="flex items-center justify-center min-h-[calc(100vh-4rem)]"><div className="text-lg">Loading your banks...</div></div></>);
  }

  return (
    <>
      <Navbar />
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div>
          <h1 className="text-3xl font-bold" data-testid="partner-portal-title">My Banks</h1>
          <p className="text-slate-600 mt-1">Welcome, {user?.name}. Here are your assigned bank accounts.</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
            <div className="text-xs text-blue-600 font-semibold">Total Assigned</div>
            <div className="text-2xl font-bold text-blue-700 mt-1" data-testid="partner-total-banks">{banks.length}</div>
          </div>
          <div className="p-4 bg-green-50 rounded-lg border border-green-100">
            <div className="text-xs text-green-600 font-semibold">Active</div>
            <div className="text-2xl font-bold text-green-700 mt-1">{activeBanks.length}</div>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div className="text-xs text-slate-600 font-semibold">Inactive</div>
            <div className="text-2xl font-bold text-slate-700 mt-1">{inactiveBanks.length}</div>
          </div>
          <div className="p-4 bg-red-50 rounded-lg border border-red-100">
            <div className="text-xs text-red-600 font-semibold">Freezed</div>
            <div className="text-2xl font-bold text-red-700 mt-1">{freezedBanks.length}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-3 flex-wrap">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search by bank, vendor, region..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="partner-bank-search" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44" data-testid="partner-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Inactive">Inactive</SelectItem>
              <SelectItem value="Freezed">Freezed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Bank Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map((bank) => (
            <Card key={bank.id} className={`hover:shadow-md transition-shadow ${bank.status?.startsWith('Freezed') ? 'border-red-200' : ''}`} data-testid={`partner-bank-card-${bank.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-semibold text-lg">{bank.bankName}</div>
                    <div className="text-sm text-slate-500 flex items-center gap-1"><Building2 className="w-3.5 h-3.5" />{bank.vendorName}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(bank.status)}
                    <Button onClick={() => handleViewBank(bank)} variant="outline" size="sm" className="gap-1" data-testid={`view-bank-${bank.id}`}>
                      <Eye className="w-3.5 h-3.5" /> View
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-slate-500 text-xs block">Account Number</span>
                    <div className="flex items-center gap-1">
                      <span className="font-mono font-medium">{bank.accountNumber}</span>
                      <button onClick={() => handleCopy(bank.accountNumber, 'Account')} className="text-slate-400 hover:text-slate-600"><Copy className="w-3 h-3" /></button>
                    </div>
                  </div>
                  <div>
                    <span className="text-slate-500 text-xs block">IFSC</span>
                    <div className="flex items-center gap-1">
                      <span className="font-mono font-medium">{bank.ifsc || '-'}</span>
                      {bank.ifsc && <button onClick={() => handleCopy(bank.ifsc, 'IFSC')} className="text-slate-400 hover:text-slate-600"><Copy className="w-3 h-3" /></button>}
                    </div>
                  </div>
                  <div>
                    <span className="text-slate-500 text-xs block">Holder</span>
                    <span className="font-medium">{bank.holderName || '-'}</span>
                  </div>
                  {bank.region && (
                    <div className="flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5 text-slate-400" />
                      <span>{bank.region}</span>
                    </div>
                  )}
                  {bank.upi && (
                    <div className="col-span-2">
                      <span className="text-slate-500 text-xs block">UPI ID</span>
                      <div className="flex items-center gap-1">
                        <span className="font-mono font-medium">{bank.upi}</span>
                        <button onClick={() => handleCopy(bank.upi, 'UPI')} className="text-slate-400 hover:text-slate-600"><Copy className="w-3 h-3" /></button>
                      </div>
                    </div>
                  )}
                </div>

                {bank.status?.startsWith('Freezed') && (
                  <div className="mt-3 p-2 bg-red-50 rounded border border-red-200 text-xs text-red-700">
                    <span className="font-semibold">Freeze Reason:</span> {bank.freezeReason || 'N/A'}
                    {bank.freezedAmount > 0 && <span className="ml-2 font-semibold">Amount: {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(bank.freezedAmount)}</span>}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full py-12 text-center text-slate-500">
              {banks.length === 0 ? 'No banks assigned to you yet.' : 'No banks match your filters.'}
            </div>
          )}
        </div>
      </div>

      <BankDetailDialogEnhanced open={showDetailDialog} onOpenChange={setShowDetailDialog} />
    </>
  );
};

export default PartnerPortal;
